import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getSubcategoriesForCategory } from '../hooks/useCategoryData';

// Shadcn UI Components
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Switch } from './ui/switch';
import EnhancedSelect from './EnhancedSelect';

// Product Components
import SupplierSkuManager from './product/SupplierSkuManager';
import BOMManager from './product/BOMManager';
import PricingTab from './product/PricingTab';

// Icons
import { 
  Package, ShoppingCart, AlertTriangle, DollarSign, Warehouse,
  Calendar, Package2, Save, CheckCircle, Clock, MapPin,
  Link2, Plus, Trash2, Check, X, Edit2, RefreshCw, Layers, BoxesIcon
} from 'lucide-react';

// API Configuration - Use runtime config
import { config } from '../config/environment';
const API = config.apiBaseUrl;
const MOCK_TENANT_ID = 'tenant-123';

// APPROVED CATEGORIES - Single source of truth from Category Reference sheet
const APPROVED_CATEGORIES = [
  'Buckets',
  'Bowls',
  'Cheese Chews',
  'Wood',
  'Dog Toys',
  'Dog Food',
  'Raw Materials'
];

const ProductMasterDetail = forwardRef(({ 
  productId, 
  item = null, 
  activeTab = 'general', 
  onDataChange = () => {}, 
  fullPageMode = false 
}, ref) => {
  // State Management
  const [productData, setProductData] = useState({
    // General Information
    name: '',
    description: '',
    category: '',
    subcategory: '',
    brand: '',
    default_warehouse: 'fba',
    status: 'ACTIVE',
    date_created: new Date().toISOString(),
    date_updated: new Date().toISOString(),
    
    // Amazon Integration
    amazon_asin: '', // Amazon Standard Identification Number (for BSR tracking)
    
    // Product Type (for BOM/Costing)
    product_type: 'finished_product', // 'raw_material' or 'finished_product'
    costing_mode: 'auto', // 'auto' or 'manual'
    
    // Raw Material Costing Fields
    raw_material_cost: 0,
    labor_cost: 0,
    import_shipping_cost: 0,
    landed_cost: 0,
    
    // Finished Product Costing Fields
    bom: [], // Bill of Materials
    component_cost: 0,
    fulfillment_cost: 0,
    // total_cogs is defined below in "Auto-calculated fields" section
    suggested_wholesale_price: 0,
    suggested_retail_price: 0,
    wholesale_price_mode: 'auto',
    retail_price_mode: 'auto',
    
    // Purchasing
    supplier_name: '',
    
    // Validation State
    validation_errors: {},
    validation_rules: {
      name: { required: true, minLength: 2 },
      category: { required: true },
      supplier_name: { required: false },
      purchase_price: { required: true, min: 0 },
      selling_price: { required: true, min: 0 }
    },
    purchase_uom: 'each',
    purchase_price: 0,
    minimum_order_qty: 1,
    lead_time_days: 7,
    supplier_sku: '',
    last_purchase_date: '',
    
    // Order Planning
    reorder_point: 10,
    target_stock_level: 50,
    preferred_warehouse: 'fba',
    
    // Sales
    selling_uom: 'each',
    default_selling_price: 0,
    channel_assignment: 'shopify',
    gross_margin: 0,
    
    // Storage
    storage_location: '',
    storage_condition: 'room_temperature',
    weight_kg: 0,
    length_cm: 0,
    width_cm: 0,
    height_cm: 0,
    warehouse_code: '',
    
    // Shelf Life
    batch_no: '',
    manufacturing_date: '',
    expiry_date: '',
    remaining_shelf_life: 0,
    shelf_life_alert: false,
    
    // Packaging
    units_per_package: 1,
    package_length: 0,
    package_width: 0,
    package_height: 0,
    package_unit: 'cm',
    weight_unit: 'lb',
    dimension_unit: 'in',
    gross_weight_kg: 0,
    net_weight_per_unit_g: 0,
    packaging_material: '',
    
    // Fulfillment Channel (for 3PL products)
    fulfillment_channel: '', // 'fba', '3pl', 'inhouse', ''
    
    // Master Carton & Pallet Specs (for 3PL shipment planning)
    carton_specs: {
      units_per_carton: 0,
      carton_length_in: 0,
      carton_width_in: 0,
      carton_height_in: 0,
      carton_weight_lb: 0,
      cartons_per_pallet: 0,
      pallet_height_in: 0,
      pallet_weight_lb: 0,
      notes: ''
    },
    
    // Pricing - CORRECTED STRUCTURE
    currency: 'USD',
    pricing_mode: 'manual',
    include_overhead: false,  // NEW: Toggle for overhead inclusion
    
    // CRITICAL FIX: Add missing pricing fields from Product Wizard
    wholesale_price: 0,
    retail_price: 0,
    cost_price: 0,
    
    // SECTION A: Manufacturing & Production
    cogm: 0,  // Cost of Goods Manufactured
    
    // SECTION B: Import & Freight (Landed Cost Components)
    intl_freight: 0,  // International Freight (Sea Freight)
    import_duties: 0,  // Import Duties / Tariffs
    inbound_handling: 0,  // Inbound Handling & Labor
    domestic_freight: 0,  // Domestic Freight (Inland Transportation)
    
    // SECTION C: Fulfillment & Logistics
    fulfillment_fee: 0,  // Fulfillment Fees (FBA/3PL)
    warehousing_cost: 0,  // Storage Cost
    outbound_freight: 0,  // Transportation to FBA
    
    // SECTION D: Marketing & Ad Spend
    ad_rate_pct: 15,  // IDS (% of Selling Price) - default 15%
    advertising_expense: 0,  // IDS Amount (calculated)
    
    // SECTION F: Revenue & Profitability
    selling_price: 0,  // Selling Price (Manual Input)
    
    // Auto-calculated fields (read-only)
    total_landed_cost: 0,  // A + B
    total_cogs: 0,  // Total COGS (A+B+C+D)
    gross_profit: 0,  // Profit (Selling - COGS)
    gross_margin_pct: 0,  // Gross Margin %
    net_margin_pct: 0  // Net Margin %
  });
  
  // CRITICAL FIX: State for dropdown options with "Add New" functionality
  const [brands, setBrands] = useState(['PetYupp', 'Generic', 'Premium', 'House Brand']);
  // ✅ FIXED: Use hardcoded APPROVED_CATEGORIES instead of API hook
  const categories = APPROVED_CATEGORIES;
  const [subcategories, setSubcategories] = useState([]);
  
  console.log('🔍 PRODUCT DETAIL - Categories for dropdown:', categories);
  
  // Update subcategories when category changes
  useEffect(() => {
    if (productData.category) {
      const validSubcategories = getSubcategoriesForCategory(productData.category);
      setSubcategories(validSubcategories);
      
      // Clear subcategory if it's not valid for the new category
      if (productData.subcategory && !validSubcategories.includes(productData.subcategory)) {
        handleFieldChange('subcategory', '');
      }
    } else {
      setSubcategories([]);
    }
  }, [productData.category]);
  const [warehouses, setWarehouses] = useState(['fba', 'awd', 'garage']);
  const [channels, setChannels] = useState(['shopify', 'amazon', 'ebay', 'direct-sales', 'wholesale']);
  const [suppliers, setSuppliers] = useState([]);
  
  // Fetch suppliers on mount
  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        // Use backendUrl + endpoint (API already includes /api/v1)
        const response = await axios.get(`${config.backendUrl}/api/v1/suppliers`, {
          headers: { 'X-Tenant-ID': MOCK_TENANT_ID }
        });
        console.log('Suppliers fetched:', response.data);
        if (response.data && Array.isArray(response.data)) {
          setSuppliers(response.data);
        } else if (response.data && response.data.suppliers) {
          setSuppliers(response.data.suppliers);
        }
      } catch (error) {
        console.error('Error fetching suppliers:', error);
      }
    };
    fetchSuppliers();
  }, []);
  
  // Helper to merge loaded data with defaults
  const mergeWithDefaults = (loadedData, defaults) => {
    const merged = { ...defaults };
    
    for (const key in loadedData) {
      if (loadedData[key] !== null && loadedData[key] !== undefined && loadedData[key] !== '') {
        merged[key] = loadedData[key];
      }
    }
    
    // Ensure critical defaults are never empty
    merged.selling_uom = merged.selling_uom || 'PCS';
    merged.storage_condition = merged.storage_condition || 'Room Temperature';
    merged.warehouse_location = merged.warehouse_location || 'Main Warehouse';
    merged.channel_assignment = merged.channel_assignment || 'Shopify';
    merged.status = merged.status || 'Active';
    merged.currency = merged.currency || 'USD';
    merged.package_unit = merged.package_unit || 'cm';
    merged.weight_unit = merged.weight_unit || 'lb';
    merged.dimension_unit = merged.dimension_unit || 'in';
    
    return merged;
  };
  
  // NEW: Overhead state from Finance Module
  const [overhead, setOverhead] = useState({
    employee_costs: {},
    employee_costs_total: 0,
    operating_expenses: {},
    operating_expenses_total: 0,
    total_overhead: 0,
    loading: false,
    error: null
  });
  
  // ============================================================
  // CHANNEL SKU ARCHITECTURE STATE
  // ============================================================
  const [channelSkus, setChannelSkus] = useState([]);
  const [channelSkusLoading, setChannelSkusLoading] = useState(false);
  const [editingChannelSku, setEditingChannelSku] = useState(null); // ID of row being edited
  const [newChannelSku, setNewChannelSku] = useState({
    channel: 'amazon_fba',
    channel_sku: '',
    asin: '',
    status: 'active'
  });
  const [showAddChannelForm, setShowAddChannelForm] = useState(false);
  
  // Channel options (should match backend CHANNEL_OPTIONS)
  const CHANNEL_OPTIONS = [
    { value: 'amazon_fba', label: '📦 Amazon FBA', prefix: 'FBA_' },
    { value: 'amazon_fbm', label: '🏭 Amazon FBM', prefix: 'FBM_' },
    { value: 'walmart', label: '🛒 Walmart', prefix: 'WM-' },
    { value: 'shopify', label: '🛍️ Shopify', prefix: '' },
    { value: 'veeqo', label: '🔗 Veeqo', prefix: 'VQ_' },
    { value: 'faire', label: '🎁 Faire', prefix: 'FAIRE_' },
    { value: 'ebay', label: '🏷️ eBay', prefix: 'EBAY_' },
    { value: 'direct', label: '🏪 Direct/Website', prefix: '' },
  ];
  
  // ============================================================
  
  // PHASE 2: Validation state - Array of error strings
  const [validationErrors, setValidationErrors] = useState([]);
  const [isValidating, setIsValidating] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [internalActiveTab, setInternalActiveTab] = useState('general');
  const [saveStatus, setSaveStatus] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Use activeTab prop in fullPageMode, internal state otherwise
  const currentActiveTab = fullPageMode ? activeTab : internalActiveTab;
  const setActiveTab = fullPageMode ? () => {} : setInternalActiveTab;

  // State for manual override of cartons_per_pallet
  const [cartonsPerPalletOverride, setCartonsPerPalletOverride] = useState(false);

  // Auto-calculate Cartons/Pallet from carton dimensions
  // Standard 48×40 inch pallet, 48 inch max stack height
  const calculateCartonsPerPallet = (length, width, height) => {
    if (!length || !width || !height || length <= 0 || width <= 0 || height <= 0) {
      return 0;
    }
    const cartonsPerLayer = Math.floor(48 / length) * Math.floor(40 / width);
    const maxLayers = Math.floor(48 / height);
    return cartonsPerLayer * maxLayers;
  };

  // Get auto-calculated value for cartons_per_pallet
  const autoCartonsPerPallet = calculateCartonsPerPallet(
    productData.carton_specs?.carton_length_in,
    productData.carton_specs?.carton_width_in,
    productData.carton_specs?.carton_height_in
  );

  // Determine if we should show auto value or manual override
  const displayCartonsPerPallet = cartonsPerPalletOverride 
    ? productData.carton_specs?.cartons_per_pallet 
    : (autoCartonsPerPallet > 0 ? autoCartonsPerPallet : productData.carton_specs?.cartons_per_pallet);

  // CRITICAL FIX: Expose save function to parent component via ref
  useImperativeHandle(ref, () => ({
    handleSave: handleSaveAll,
    hasUnsavedChanges: hasUnsavedChanges,
    validateProductData: validateProductData
  }));

  // Currency symbol helper
  const getCurrencySymbol = (currency) => {
    const symbols = {
      'USD': '$',
      'INR': '₹',
      'EUR': '€',
      'GBP': '£',
      'AUD': 'A$',
      'CAD': 'C$',
      'JPY': '¥'
    };
    return symbols[currency] || '$';
  };

  // Initialize data from item prop if provided
  useEffect(() => {
    if (item && fullPageMode) {
      console.log('🔍 ProductMasterDetail: Loading item data:', item);
      console.log('🔍 Item ID:', item.id, 'SKU:', item.sku);
      console.log('🔍 Pricing data in item:', {
        wholesale_price: item.wholesale_price,
        retail_price: item.retail_price,
        cost_price: item.cost_price,
        pricing_mode: item.pricing_mode
      });
      
      // CRITICAL FIX: Read from NEW inventory structure OR fall back to OLD structures
      const inventoryData = item.inventory || {};
      const planningData = item.planning || {};
      const storageData = item.storage || {};
      const packData = item.pack || {};
      
      console.log('📦 Loading inventory data from:', {
        hasNewStructure: !!item.inventory,
        hasOldStructures: !!(item.planning || item.storage || item.pack),
        inventory: inventoryData,
        planning: planningData,
        storage: storageData
      });
      
      console.log('📦 CURRENT STOCK LOADING DEBUG:');
      console.log('  - inventoryData.current_stock:', inventoryData.current_stock);
      console.log('  - planningData.on_hand:', planningData.on_hand);
      console.log('  - item.current_stock:', item.current_stock);
      console.log('  - Will use:', inventoryData.current_stock ?? planningData.on_hand ?? item.current_stock ?? 0);
      
      setProductData(prev => {
        const newData = {
          ...prev,
          ...item,
          name: item.name || item.title || prev.name,
          sku: item.sku || item.sku_root || prev.sku,
          // CRITICAL FIX: Read from category_id (database field) not category
          category: item.category_id || item.category || prev.category,
          subcategory: item.subcategory_id || item.subcategory || prev.subcategory,
          
          // Product Type & Costing Mode
          product_type: item.product_type || prev.product_type || 'finished_product',
          costing_mode: item.costing_mode || prev.costing_mode || 'auto',
          
          // Raw Material Costing Fields
          raw_material_cost: item.raw_material_cost ?? prev.raw_material_cost ?? 0,
          labor_cost: item.labor_cost ?? prev.labor_cost ?? 0,
          import_shipping_cost: item.import_shipping_cost ?? prev.import_shipping_cost ?? 0,
          landed_cost: item.landed_cost ?? prev.landed_cost ?? 0,
          
          // Finished Product Costing Fields
          bom: item.bom || prev.bom || [],
          component_cost: item.component_cost ?? prev.component_cost ?? 0,
          fulfillment_cost: item.fulfillment_cost ?? prev.fulfillment_cost ?? 0,
          total_cogs: item.total_cogs ?? prev.total_cogs ?? 0,
          suggested_wholesale_price: item.suggested_wholesale_price ?? prev.suggested_wholesale_price ?? 0,
          suggested_retail_price: item.suggested_retail_price ?? prev.suggested_retail_price ?? 0,
          wholesale_price_mode: item.wholesale_price_mode || prev.wholesale_price_mode || 'auto',
          retail_price_mode: item.retail_price_mode || prev.retail_price_mode || 'auto',
          
          // CRITICAL FIX: Explicitly set pricing fields
          wholesale_price: item.wholesale_price ?? prev.wholesale_price,
          retail_price: item.retail_price ?? prev.retail_price,
          cost_price: item.cost_price ?? prev.cost_price,
          pricing_mode: item.pricing_mode || prev.pricing_mode,
          
          // CRITICAL FIX: Load inventory fields from NEW structure OR OLD structures
          current_stock: inventoryData.current_stock ?? planningData.on_hand ?? item.current_stock ?? prev.current_stock ?? 0,
          reorder_point: inventoryData.reorder_point ?? planningData.reorder_point ?? item.reorder_point ?? prev.reorder_point ?? 0,
          target_stock_level: inventoryData.target_stock_level ?? planningData.target_stock ?? item.target_stock_level ?? prev.target_stock_level ?? 0,
          warehouse_code: inventoryData.warehouse_code ?? storageData.warehouse_code ?? item.warehouse_code ?? prev.warehouse_code ?? '',
          storage_location: inventoryData.storage_location ?? storageData.location ?? item.storage_location ?? prev.storage_location ?? '',
          storage_condition: inventoryData.storage_condition ?? storageData.condition ?? item.storage_condition ?? prev.storage_condition ?? 'Room Temperature',
          preferred_warehouse: inventoryData.preferred_warehouse ?? planningData.preferred_warehouse ?? item.preferred_warehouse ?? prev.preferred_warehouse ?? '',
          length: inventoryData.length ?? item.length ?? prev.length ?? 0,
          width: inventoryData.width ?? item.width ?? prev.width ?? 0,
          height: inventoryData.height ?? item.height ?? prev.height ?? 0,
          weight: inventoryData.weight ?? packData.weightKg ?? item.weight ?? prev.weight ?? 0,
          units_per_package: inventoryData.units_per_package ?? packData.unitsPerPackage ?? item.units_per_package ?? prev.units_per_package ?? 1,
          packaging_material: inventoryData.packaging_material ?? item.packaging_material ?? prev.packaging_material ?? '',
          batch_number: inventoryData.batch_number ?? item.batch_number ?? prev.batch_number ?? '',
          manufacturing_date: inventoryData.manufacturing_date ?? item.manufacturing_date ?? prev.manufacturing_date ?? null,
          expiry_date: inventoryData.expiry_date ?? item.expiry_date ?? prev.expiry_date ?? null,
          weight_unit: inventoryData.weight_unit ?? item.weight_unit ?? prev.weight_unit ?? 'lb',
          dimension_unit: inventoryData.dimension_unit ?? item.dimension_unit ?? prev.dimension_unit ?? 'in',
          
          // Fulfillment Channel
          fulfillment_channel: item.fulfillment_channel ?? prev.fulfillment_channel ?? '',
          
          // Master Carton & Pallet Specs - pre-fill from product dimensions if carton specs are empty
          carton_specs: (() => {
            const existingSpecs = item.carton_specs ?? prev.carton_specs ?? {};
            // If carton specs already have meaningful data, use them
            if (existingSpecs.units_per_carton > 0 || existingSpecs.carton_length_in > 0) {
              return existingSpecs;
            }
            // Otherwise, pre-fill carton dimensions from product dimensions as starting point
            // Dimensions are stored in inventoryData (from inventory object)
            const productLength = inventoryData.length ?? packData.length ?? item.package_length ?? 0;
            const productWidth = inventoryData.width ?? packData.width ?? item.package_width ?? 0;
            const productHeight = inventoryData.height ?? packData.height ?? item.package_height ?? 0;
            const productWeight = inventoryData.weight ?? packData.weightKg ?? item.weight ?? 0;
            return {
              units_per_carton: existingSpecs.units_per_carton || 0,
              carton_length_in: existingSpecs.carton_length_in || productLength || 0,
              carton_width_in: existingSpecs.carton_width_in || productWidth || 0,
              carton_height_in: existingSpecs.carton_height_in || productHeight || 0,
              carton_weight_lb: existingSpecs.carton_weight_lb || productWeight || 0,
              cartons_per_pallet: existingSpecs.cartons_per_pallet || 0,
              pallet_height_in: existingSpecs.pallet_height_in || 0,
              pallet_weight_lb: existingSpecs.pallet_weight_lb || 0,
              notes: existingSpecs.notes || ''
            };
          })(),
        };
        console.log('✅ ProductMasterDetail: ProductData updated with pricing and inventory:', {
          wholesale_price: newData.wholesale_price,
          retail_price: newData.retail_price,
          cost_price: newData.cost_price,
          current_stock: newData.current_stock,
          reorder_point: newData.reorder_point,
          target_stock_level: newData.target_stock_level,
          fulfillment_channel: newData.fulfillment_channel,
          carton_specs: newData.carton_specs
        });
        console.log('✅ FINAL STATE - Current Stock will be set to:', newData.current_stock);
        return newData;
      });
    }
  }, [item, fullPageMode]);

  // Handle field changes (NO AUTO-SAVE)
  const handleFieldChange = (field, value) => {
    console.log('🔵 FIELD CHANGED:', field, '=', value, 'Type:', typeof value);
    
    // Special logging for inventory fields
    if (field === 'current_stock') {
      console.log('📦 CURRENT STOCK CHANGED:');
      console.log('  - New value:', value);
      console.log('  - Type:', typeof value);
      console.log('  - Previous value:', productData.current_stock);
    }
    
    setProductData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Log the updated state for critical fields
      if (field === 'current_stock') {
        console.log('  - Updated productData.current_stock:', updated.current_stock);
      }
      
      return updated;
    });
    
    setHasUnsavedChanges(true);
    // CRITICAL FIX: Notify parent component of changes
    onDataChange();
  };
  
  // Handler for nested carton_specs field changes
  const handleCartonSpecsChange = (field, value) => {
    console.log('📦 CARTON SPECS CHANGED:', field, '=', value);
    setProductData(prev => {
      const newSpecs = {
        ...prev.carton_specs,
        [field]: value
      };
      
      // If carton dimensions changed and not manually overridden, auto-update cartons_per_pallet
      if (['carton_length_in', 'carton_width_in', 'carton_height_in'].includes(field) && !cartonsPerPalletOverride) {
        const autoValue = calculateCartonsPerPallet(
          field === 'carton_length_in' ? value : newSpecs.carton_length_in,
          field === 'carton_width_in' ? value : newSpecs.carton_width_in,
          field === 'carton_height_in' ? value : newSpecs.carton_height_in
        );
        if (autoValue > 0) {
          newSpecs.cartons_per_pallet = autoValue;
        }
      }
      
      return {
        ...prev,
        carton_specs: newSpecs
      };
    });
    setHasUnsavedChanges(true);
    onDataChange();
  };
  
  // CRITICAL FIX: Handlers for adding new dropdown items
  const handleAddBrand = async (newBrand) => {
    setBrands(prev => [...prev, newBrand]);
    handleFieldChange('brand', newBrand);
    
    // Optionally save to backend
    try {
      await axios.post(`${API}/brands`, {
        name: newBrand,
        tenant_id: MOCK_TENANT_ID
      });
      console.log('✅ New brand saved to backend:', newBrand);
    } catch (error) {
      console.error('Error saving brand:', error);
    }
  };

  const handleAddCategory = async (newCategory) => {
    setCategories(prev => [...prev, newCategory]);
    handleFieldChange('category', newCategory);
  };

  const handleAddSubcategory = async (newSubcategory) => {
    setSubcategories(prev => [...prev, newSubcategory]);
    handleFieldChange('subcategory', newSubcategory);
  };

  const handleAddWarehouse = async (newWarehouse) => {
    setWarehouses(prev => [...prev, newWarehouse]);
    handleFieldChange('preferred_warehouse', newWarehouse);
  };

  const handleAddChannel = async (newChannel) => {
    setChannels(prev => [...prev, newChannel]);
    handleFieldChange('channel_assignment', newChannel);
  };

  const handleNumericFieldChange = (field, event) => {
    const value = event.target.value;
    
    // Remove any non-numeric characters except decimal point and minus (for initial input)
    const cleanedValue = value.replace(/[^0-9.]/g, '');
    
    // Prevent multiple decimal points
    const parts = cleanedValue.split('.');
    const finalValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : cleanedValue;
    
    // Update the input value directly to show only valid characters
    event.target.value = finalValue;
    
    // Parse and update state
    if (finalValue === '' || finalValue === '.') {
      handleFieldChange(field, 0);
    } else {
      const numValue = parseFloat(finalValue);
      if (!isNaN(numValue) && numValue >= 0) {
        handleFieldChange(field, numValue);
      }
    }
  };

  // Calculate pricing totals and margins in real-time - CORRECTED FORMULAS
  const calculatePricingMetrics = () => {
    // Parse all cost components
    const cogm = parseFloat(productData.cogm || 0);
    const intlFreight = parseFloat(productData.intl_freight || 0);
    const importDuties = parseFloat(productData.import_duties || 0);
    const inboundHandling = parseFloat(productData.inbound_handling || 0);
    const domesticFreight = parseFloat(productData.domestic_freight || 0);
    const fulfillmentFee = parseFloat(productData.fulfillment_fee || 0);
    const warehousingCost = parseFloat(productData.warehousing_cost || 0);
    const outboundFreight = parseFloat(productData.outbound_freight || 0);
    const sellingPrice = parseFloat(productData.selling_price || 0);
    const adRatePct = parseFloat(productData.ad_rate_pct || 15);

    // FORMULA: Total Landed Cost = COGM + International Freight + Import Duties + Inbound Handling + Domestic Freight
    const totalLandedCost = cogm + intlFreight + importDuties + inboundHandling + domesticFreight;

    // FORMULA: IDS Amount = (Selling Price × IDS%) / 100
    const advertisingExpense = (sellingPrice * adRatePct) / 100;

    // FORMULA: Total COGS = Total Landed Cost + Fulfillment + Storage + Transportation + IDS Amount
    const totalCOGS = totalLandedCost + fulfillmentFee + warehousingCost + outboundFreight + advertisingExpense;

    // FORMULA: Profit = Selling Price - Total COGS
    const grossProfit = sellingPrice - totalCOGS;

    // FORMULA: Gross Margin % = (Profit ÷ Selling Price) × 100
    const grossMarginPct = sellingPrice > 0 
      ? (grossProfit / sellingPrice) * 100 
      : 0;

    // FORMULA: Net Margin % = ((Profit - Ad&Ops) ÷ Selling Price) × 100
    // For now, using same as gross margin (can be enhanced later with additional operating costs)
    const netMarginPct = sellingPrice > 0
      ? ((grossProfit - advertisingExpense) / sellingPrice) * 100
      : 0;

    // Update state with calculated values
    setProductData(prev => ({
      ...prev,
      total_landed_cost: (Number(totalLandedCost) || 0).toFixed(2),
      advertising_expense: (Number(advertisingExpense) || 0).toFixed(2),
      total_cogs: (Number(totalCOGS) || 0).toFixed(2),
      gross_profit: (Number(grossProfit) || 0).toFixed(2),
      gross_margin_pct: (Number(grossMarginPct) || 0).toFixed(2),
      net_margin_pct: (Number(netMarginPct) || 0).toFixed(2)
    }));
  };

  // Trigger calculation when pricing fields change
  useEffect(() => {
    calculatePricingMetrics();
  }, [
    productData.cogm,
    productData.intl_freight,
    productData.import_duties,
    productData.inbound_handling,
    productData.domestic_freight,
    productData.fulfillment_fee,
    productData.warehousing_cost,
    productData.outbound_freight,
    productData.selling_price,
    productData.ad_rate_pct
  ]);

  // PHASE 2: Enhanced validation function - RETURNS ARRAY OF ERROR STRINGS
  const validateProductData = () => {
    const errors = []; // Array, not object!
    
    console.log('═══════════════════════════════════════════════════════════════');
    console.log('🔍 VALIDATION DEBUG - STARTING');
    console.log('═══════════════════════════════════════════════════════════════');
    console.log('📋 COMPLETE PRODUCT DATA:', JSON.stringify(productData, null, 2));
    console.log('═══════════════════════════════════════════════════════════════');
    
    // ===== GENERAL TAB VALIDATIONS =====
    console.log('🔍 Checking GENERAL TAB fields:');
    
    // Required field: Product Name
    console.log('  - name:', productData.name, 'length:', productData.name?.trim().length);
    if (!productData.name || productData.name.trim().length < 2) {
      errors.push('Product name is required (minimum 2 characters) - General tab');
      console.log('    ❌ VALIDATION ERROR: Product name invalid');
    }
    
    // Required field: Category
    console.log('  - category:', productData.category);
    if (!productData.category || productData.category.trim() === '') {
      errors.push('Category is required - General tab');
      console.log('    ❌ VALIDATION ERROR: Category required');
    }
    
    // Required field: Subcategory
    console.log('  - subcategory:', productData.subcategory);
    if (!productData.subcategory || productData.subcategory.trim() === '') {
      errors.push('Subcategory is required - General tab');
      console.log('    ❌ VALIDATION ERROR: Subcategory required');
    }
    
    // Status validation
    console.log('  - status:', productData.status);
    if (!productData.status || productData.status.trim() === '') {
      errors.push('Status is required - General tab');
      console.log('    ❌ VALIDATION ERROR: Status required');
    }
    
    // ===== PRICING TAB VALIDATIONS =====
    console.log('🔍 Checking PRICING TAB fields:');
    
    // Check if any pricing data exists
    const hasPricingData = productData.wholesale_price || productData.retail_price || productData.cost_price;
    console.log('  - hasPricingData:', hasPricingData);
    console.log('  - wholesale_price:', productData.wholesale_price);
    console.log('  - retail_price:', productData.retail_price);
    console.log('  - cost_price:', productData.cost_price);
    console.log('  - pricing_mode:', productData.pricing_mode);
    console.log('  - currency:', productData.currency);
    
    // Pricing Mode is required ONLY if any pricing fields are set
    if (hasPricingData && (!productData.pricing_mode || productData.pricing_mode.trim() === '')) {
      errors.push('Pricing Mode is required when pricing data exists - Pricing tab');
      console.log('    ❌ VALIDATION ERROR: Pricing Mode required (has pricing data)');
    }
    
    // Validate pricing fields based on pricing mode
    if (productData.pricing_mode === 'manual') {
      console.log('  - Validating manual pricing mode fields...');
      
      // Wholesale Price validation
      const wholesalePrice = parseFloat(productData.wholesale_price || 0);
      console.log('    - wholesalePrice parsed:', wholesalePrice);
      
      if (isNaN(wholesalePrice) || wholesalePrice < 0) {
        errors.push('Wholesale Price must be 0 or greater - Pricing tab');
        console.log('      ❌ VALIDATION ERROR: Wholesale Price invalid');
      }
      
      // Retail Price validation
      const retailPrice = parseFloat(productData.retail_price || 0);
      console.log('    - retailPrice parsed:', retailPrice);
      
      if (isNaN(retailPrice) || retailPrice < 0) {
        errors.push('Retail Price must be 0 or greater - Pricing tab');
        console.log('      ❌ VALIDATION ERROR: Retail Price invalid');
      }
      
      // Cost Price validation
      const costPrice = parseFloat(productData.cost_price || 0);
      console.log('    - costPrice parsed:', costPrice);
      
      if (isNaN(costPrice) || costPrice < 0) {
        errors.push('Cost Price must be 0 or greater - Pricing tab');
        console.log('      ❌ VALIDATION ERROR: Cost Price invalid');
      }
      
      // Business logic validation: Retail should be >= Wholesale
      if (!isNaN(wholesalePrice) && !isNaN(retailPrice) && wholesalePrice > 0 && retailPrice > 0 && retailPrice < wholesalePrice) {
        errors.push('Retail Price should be greater than or equal to Wholesale Price - Pricing tab');
        console.log('      ❌ VALIDATION ERROR: Retail < Wholesale');
      }
      
      // Warning if prices are unusually low
      if (!isNaN(wholesalePrice) && !isNaN(retailPrice) && wholesalePrice > 0 && retailPrice > 0) {
        const margin = ((retailPrice - wholesalePrice) / retailPrice) * 100;
        if (margin < 5) {
          errors.push('⚠️ Warning: Profit margin is very low (less than 5%) - Pricing tab');
          console.log('      ⚠️ WARNING: Low margin');
        }
      }
    }
    
    // Currency is required ONLY if pricing fields exist
    if (hasPricingData && (!productData.currency || productData.currency.trim() === '')) {
      errors.push('Currency is required when pricing data exists - Pricing tab');
      console.log('    ❌ VALIDATION ERROR: Currency required (has pricing data)');
    }
    
    console.log('═══════════════════════════════════════════════════════════════');
    console.log('✅ VALIDATION COMPLETE');
    console.log('Total errors found:', errors.length);
    if (errors.length > 0) {
      console.log('❌ VALIDATION ERRORS:');
      errors.forEach((error, index) => {
        console.log(`  ${index + 1}. ${error}`);
      });
    } else {
      console.log('✅ NO VALIDATION ERRORS');
    }
    console.log('═══════════════════════════════════════════════════════════════');
    
    // ===== PURCHASING TAB VALIDATIONS =====
    // Purchase Price validation (only if exists)
    if (productData.purchase_price !== undefined && productData.purchase_price !== null) {
      const purchasePrice = parseFloat(productData.purchase_price);
      if (isNaN(purchasePrice) || purchasePrice < 0) {
        errors.push('Purchase Price must be 0 or greater - Purchasing tab');
      }
    }
    
    // Minimum Order Quantity validation (only if exists)
    if (productData.minimum_order_qty !== undefined && productData.minimum_order_qty !== null) {
      const minOrderQty = parseInt(productData.minimum_order_qty);
      if (isNaN(minOrderQty) || minOrderQty < 1) {
        errors.push('Minimum Order Quantity must be at least 1 - Purchasing tab');
      }
    }
    
    // Lead Time validation (only if exists)
    if (productData.lead_time_days !== undefined && productData.lead_time_days !== null) {
      const leadTime = parseInt(productData.lead_time_days);
      if (isNaN(leadTime) || leadTime < 0) {
        errors.push('Lead Time must be 0 or greater - Purchasing tab');
      }
    }
    
    // ===== PLANNING TAB VALIDATIONS =====
    // Reorder Point validation (only if exists)
    console.log('🔍 Checking PLANNING/INVENTORY TAB fields:');
    console.log('  - reorder_point:', productData.reorder_point);
    console.log('  - target_stock_level:', productData.target_stock_level);
    
    if (productData.reorder_point !== undefined && productData.reorder_point !== null && productData.reorder_point !== '') {
      const reorderPoint = parseInt(productData.reorder_point);
      console.log('    - reorderPoint parsed:', reorderPoint);
      
      if (isNaN(reorderPoint) || reorderPoint < 0) {
        errors.push('Reorder Point must be 0 or greater - Inventory tab');
        console.log('      ❌ VALIDATION ERROR: Reorder Point invalid');
      }
    }
    
    // Target Stock Level validation (only if exists and is non-zero)
    if (productData.target_stock_level !== undefined && productData.target_stock_level !== null && productData.target_stock_level !== '') {
      const targetStock = parseInt(productData.target_stock_level);
      console.log('    - targetStock parsed:', targetStock);
      
      if (isNaN(targetStock) || targetStock < 0) {
        errors.push('Target Stock Level must be 0 or greater - Inventory tab');
        console.log('      ❌ VALIDATION ERROR: Target Stock invalid');
      }
      
      // Business logic: Target Stock should be >= Reorder Point
      // BUT ONLY if both are set to meaningful positive values
      const reorderPoint = parseInt(productData.reorder_point || 0);
      console.log('    - Business logic check: targetStock:', targetStock, 'reorderPoint:', reorderPoint);
      
      if (targetStock > 0 && reorderPoint > 0 && targetStock < reorderPoint) {
        const errorMsg = `Target Stock Level (${targetStock}) should be greater than or equal to Reorder Point (${reorderPoint}) - Inventory tab`;
        errors.push(errorMsg);
        console.log('      ❌ VALIDATION ERROR:', errorMsg);
      }
    }
    
    // ===== SALES TAB VALIDATIONS =====
    // Default Selling Price validation (only if exists)
    if (productData.default_selling_price !== undefined && productData.default_selling_price !== null) {
      const sellingPrice = parseFloat(productData.default_selling_price);
      if (isNaN(sellingPrice) || sellingPrice < 0) {
        errors.push('Default Selling Price must be 0 or greater - Sales tab');
      }
    }
    
    return errors; // Return array of error strings
  };
  
  // Manual save function for "Save All" button
  const handleSaveAll = async () => {
    console.log('💾 SAVE BUTTON CLICKED - hasUnsavedChanges:', hasUnsavedChanges);
    
    // ===== CRITICAL DEBUG LOGGING =====
    console.log('=== 💾 SAVE HANDLER - Form Data ===');
    console.log('   SKU:', productData.sku);
    console.log('   Category:', productData.category);
    console.log('   Subcategory:', productData.subcategory);
    console.log('   Product Name:', productData.name);
    console.log('   Full productData:', productData);
    console.log('=================================');
    
    // Allow save even if no changes for testing purposes
    // if (!hasUnsavedChanges) {
    //   toast.info('No changes to save');
    //   return;
    // }
    
    // PHASE 2: Validate before saving
    setIsValidating(true);
    const errors = validateProductData(); // Returns array of error strings
    setIsValidating(false);
    
    if (errors.length > 0) {
      console.log('❌ VALIDATION FAILED - Stopping save');
      console.log('❌ Validation errors array:', errors);
      console.log('❌ Number of errors:', errors.length);
      
      // Store errors for display
      setValidationErrors(errors);
      
      toast.error(`Please fix ${errors.length} validation error${errors.length > 1 ? 's' : ''} before saving`);
      // Scroll to top to show error summary
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return; // CRITICAL: Stop here, do not proceed with save
    }
    
    console.log('✅ VALIDATION PASSED - Proceeding with save');
    
    // Clear any previous validation errors
    setValidationErrors([]);
    
    try {
      setSaveStatus('saving');
      
      // CRITICAL FIX: Dual-write to both NEW (inventory) and OLD (planning/storage/packaging) structures
      const dataToSave = {
        ...productData,
        
        // Ensure critical defaults
        selling_uom: productData.selling_uom || 'PCS',
        storage_condition: productData.storage_condition || 'Room Temperature',
        warehouse_location: productData.warehouse_location || 'Main Warehouse',
        channel_assignment: productData.channel_assignment || 'Shopify',
        status: productData.status || 'Active',
        currency: productData.currency || 'USD',
        package_unit: productData.package_unit || 'cm',
        weight_unit: productData.weight_unit || 'kg',
        dimension_unit: productData.dimension_unit || 'cm',
        tenant_id: MOCK_TENANT_ID,
        updated_at: new Date().toISOString(),
        
        // NEW: Consolidated inventory structure
        inventory: {
          current_stock: productData.current_stock || 0,
          reorder_point: productData.reorder_point || 0,
          target_stock_level: productData.target_stock_level || 0,
          warehouse_code: productData.warehouse_code || '',
          storage_location: productData.storage_location || '',
          storage_condition: productData.storage_condition || 'Room Temperature',
          preferred_warehouse: productData.preferred_warehouse || '',
          length: productData.length || 0,
          width: productData.width || 0,
          height: productData.height || 0,
          weight: productData.weight || 0,
          weight_unit: productData.weight_unit || 'kg',
          dimension_unit: productData.dimension_unit || 'cm',
          units_per_package: productData.units_per_package || 1,
          packaging_material: productData.packaging_material || '',
          batch_number: productData.batch_number || '',
          manufacturing_date: productData.manufacturing_date || null,
          expiry_date: productData.expiry_date || null
        },
        
        // OLD: Keep backward compatibility with legacy structure
        planning: {
          reorder_point: productData.reorder_point || 0,
          target_stock: productData.target_stock_level || 0,
          on_hand: productData.current_stock || 0
        },
        
        storage: {
          warehouse_code: productData.warehouse_code || '',
          location: productData.storage_location || '',
          condition: productData.storage_condition || 'Room Temperature'
        },
        
        pack: {
          unitsPerPackage: productData.units_per_package || 1,
          weightKg: productData.weight || 0
        },
        
        // Fulfillment Channel
        fulfillment_channel: productData.fulfillment_channel || '',
        
        // Master Carton & Pallet Specs
        carton_specs: productData.carton_specs || {
          units_per_carton: 0,
          carton_length_in: 0,
          carton_width_in: 0,
          carton_height_in: 0,
          carton_weight_lb: 0,
          cartons_per_pallet: 0,
          pallet_height_in: 0,
          pallet_weight_lb: 0,
          notes: ''
        }
      };
      
      // CRITICAL FIX: Map category/subcategory to category_id/subcategory_id for database
      console.log('=== 🔄 BEFORE MAPPING ===');
      console.log('   dataToSave.category:', dataToSave.category);
      console.log('   dataToSave.subcategory:', dataToSave.subcategory);
      
      if (dataToSave.category && dataToSave.category !== 'Unknown') {
        dataToSave.category_id = dataToSave.category;
        console.log('✅ Mapped category → category_id:', dataToSave.category_id);
      }
      if (dataToSave.subcategory) {
        dataToSave.subcategory_id = dataToSave.subcategory;
        console.log('✅ Mapped subcategory → subcategory_id:', dataToSave.subcategory_id);
      }
      
      console.log('=== 📤 API REQUEST PAYLOAD ===');
      console.log('   SKU:', dataToSave.sku);
      console.log('   category:', dataToSave.category);
      console.log('   category_id:', dataToSave.category_id);
      console.log('   subcategory:', dataToSave.subcategory);
      console.log('   subcategory_id:', dataToSave.subcategory_id);
      console.log('   Full payload (updates):', JSON.stringify({ updates: dataToSave }, null, 2));
      console.log('================================');
      
      // Make actual API call to save product data using PUT for bulk update
      console.log('🌐 Making API Call...');
      console.log('  URL:', `${API}/product-master/item/${productId}`);
      console.log('  Method: PUT');
      
      const response = await axios.put(
        `${API}/product-master/item/${productId}`,
        { updates: dataToSave },  // Wrap in updates object as expected by backend
        {
          params: { tenant_id: MOCK_TENANT_ID },
          headers: {
            'X-Tenant-ID': MOCK_TENANT_ID,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('=== 📥 API RESPONSE ===');
      console.log('   Status:', response.status);
      console.log('   Data:', response.data);
      console.log('   Message:', response.data.message);
      console.log('   Fields Updated:', response.data.fields_updated);
      console.log('   Full Response:', JSON.stringify(response.data, null, 2));
      console.log('======================');
      
      setSaveStatus('saved');
      setHasUnsavedChanges(false);
      
      // CRITICAL: Dispatch events to refresh Product Master list
      window.dispatchEvent(new CustomEvent('refreshProductList', {
        detail: { productId, updatedData: dataToSave }
      }));
      
      window.dispatchEvent(new CustomEvent('productUpdated', {
        detail: { 
          id: productId,
          sku: dataToSave.sku || dataToSave.sku_root,
          name: dataToSave.name || dataToSave.product_name,
          tenant_id: dataToSave.tenant_id,
          ...dataToSave
        }
      }));
      
      console.log('✅ Product saved successfully and refresh events dispatched');
      
      toast.success('Product updated successfully!');
      
      setTimeout(() => setSaveStatus(''), 2000);
    } catch (error) {
      setSaveStatus('error');
      console.error('❌ Save error:', error);
      console.error('❌ Save error response:', error.response?.data);
      console.error('❌ Save error status:', error.response?.status);
      console.error('❌ Save error headers:', error.response?.headers);
      console.error('❌ Data that was sent:', dataToSave);
      toast.error(`Save failed: ${error.response?.data?.detail || error.message}`);
      setTimeout(() => setSaveStatus(''), 3000);
    }
  };

  // Load PO-based pricing for Allocation Mode
  const handleLoadPOPricing = async () => {
    if (!productData.sku) {
      toast.warning('⚠️ SKU is required to load Purchase Order pricing');
      return;
    }
    
    try {
      setLoading(true);
      console.log('📦 Loading PO-based pricing for SKU:', productData.sku);
      
      const response = await axios.get(`${API}/pricing/po-based/${productData.sku}`, {
        params: { tenant_id: MOCK_TENANT_ID }
      });
      
      if (response.data.success) {
        // Populate pricing fields from PO data
        setProductData(prev => ({
          ...prev,
          cogm: response.data.cogm || 0,
          intl_freight: response.data.intl_freight || 0,
          import_duties: response.data.import_duties || 0,
          inbound_handling: response.data.inbound_handling || 0,
          domestic_freight: response.data.domestic_freight || 0,
          // Note: Fulfillment and Marketing fields remain user-editable
        }));
        
        setHasUnsavedChanges(true);
        toast.success(`✅ Pricing loaded from Purchase Order: ${response.data.source_po_number}`, {
          description: `Allocation Method: ${response.data.allocation_method}`
        });
      } else {
        toast.warning(`⚠️ ${response.data.message}`, {
          description: 'Please ensure you have received Purchase Orders for this SKU, or switch to Manual mode.'
        });
      }
      
    } catch (error) {
      console.error('Error loading PO pricing:', error);
      toast.error('❌ Error loading Purchase Order pricing', {
        description: 'Please check console for details.'
      });
    } finally {
      setLoading(false);
    }
  };

  // NEW: Fetch overhead allocation from Finance Module
  const fetchOverheadAllocation = async () => {
    if (!productData.sku) {
      console.log('⚠️ No SKU available for overhead calculation');
      return;
    }
    
    try {
      setOverhead(prev => ({ ...prev, loading: true, error: null }));
      console.log('🏢 Fetching overhead allocation for SKU:', productData.sku);
      
      const response = await axios.get(`${API}/finance/overhead-allocation/${productData.sku}`, {
        params: { 
          tenant_id: MOCK_TENANT_ID,
          period: '2025-10'
        }
      });
      
      if (response.data.success) {
        const overheadData = response.data.data;
        setOverhead({
          employee_costs: overheadData.employee_costs || {},
          employee_costs_total: overheadData.employee_costs_total || 0,
          operating_expenses: overheadData.operating_expenses || {},
          operating_expenses_total: overheadData.operating_expenses_total || 0,
          total_overhead: overheadData.total_overhead || 0,
          loading: false,
          error: null
        });
        console.log('✅ Overhead loaded:', overheadData.total_overhead);
      }
      
    } catch (error) {
      console.error('Error fetching overhead:', error);
      setOverhead(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to load overhead allocation'
      }));
    }
  };

  // NEW: Effect to fetch overhead when include_overhead is toggled
  React.useEffect(() => {
    if (productData.include_overhead && productData.sku) {
      fetchOverheadAllocation();
    }
  }, [productData.include_overhead, productData.sku]);

  // ============================================================
  // CHANNEL SKU ARCHITECTURE FUNCTIONS
  // ============================================================
  
  // Fetch channel SKUs for the current product
  const fetchChannelSkus = async () => {
    if (!productData.sku && !productData.master_sku) return;
    
    const masterSku = productData.master_sku || productData.sku;
    setChannelSkusLoading(true);
    
    try {
      const response = await axios.get(`${API}/products/${masterSku}/channel-skus`, {
        headers: { 'X-Tenant-ID': MOCK_TENANT_ID }
      });
      
      setChannelSkus(response.data.channel_skus || []);
      console.log('📡 Channel SKUs loaded:', response.data.channel_skus?.length || 0);
    } catch (error) {
      console.error('Error fetching channel SKUs:', error);
      // Don't show error toast for 404 (product may not have any mappings yet)
      if (error.response?.status !== 404) {
        toast.error('Failed to load channel SKU mappings');
      }
    } finally {
      setChannelSkusLoading(false);
    }
  };
  
  // Add a new channel SKU mapping
  const handleAddChannelSku = async () => {
    if (!newChannelSku.channel_sku.trim()) {
      toast.error('Channel SKU is required');
      return;
    }
    
    const masterSku = productData.master_sku || productData.sku;
    if (!masterSku) {
      toast.error('Product SKU is required');
      return;
    }
    
    // Sanitize channel SKU - remove trailing .0 from numeric-looking values
    let sanitizedSku = newChannelSku.channel_sku.trim();
    if (/^\d+\.0$/.test(sanitizedSku)) {
      sanitizedSku = sanitizedSku.replace(/\.0$/, '');
      toast.info(`SKU sanitized: removed .0 suffix`);
    }
    
    try {
      setChannelSkusLoading(true);
      
      const response = await axios.post(
        `${API}/products/${masterSku}/channel-skus`,
        {
          channel: newChannelSku.channel,
          channel_sku: sanitizedSku,
          asin: newChannelSku.asin || null,
          status: newChannelSku.status
        },
        { headers: { 'X-Tenant-ID': MOCK_TENANT_ID } }
      );
      
      toast.success(`Channel SKU ${sanitizedSku} added`);
      
      // Reset form and refresh list
      setNewChannelSku({ channel: 'amazon_fba', channel_sku: '', asin: '', status: 'active' });
      setShowAddChannelForm(false);
      await fetchChannelSkus();
      
    } catch (error) {
      console.error('Error adding channel SKU:', error);
      const message = error.response?.data?.detail || 'Failed to add channel SKU';
      toast.error(message);
    } finally {
      setChannelSkusLoading(false);
    }
  };
  
  // Update a channel SKU mapping (inline edit)
  const handleUpdateChannelSku = async (channelSku, updates) => {
    try {
      setChannelSkusLoading(true);
      
      await axios.put(
        `${API}/sku-mappings/${channelSku}`,
        updates,
        { headers: { 'X-Tenant-ID': MOCK_TENANT_ID } }
      );
      
      toast.success('Channel SKU updated');
      setEditingChannelSku(null);
      await fetchChannelSkus();
      
    } catch (error) {
      console.error('Error updating channel SKU:', error);
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setChannelSkusLoading(false);
    }
  };
  
  // Delete a channel SKU mapping
  const handleDeleteChannelSku = async (channelSku) => {
    if (!window.confirm(`Delete channel SKU mapping '${channelSku}'?`)) return;
    
    try {
      setChannelSkusLoading(true);
      
      await axios.delete(`${API}/sku-mappings/${channelSku}`, {
        headers: { 'X-Tenant-ID': MOCK_TENANT_ID }
      });
      
      toast.success('Channel SKU deleted');
      await fetchChannelSkus();
      
    } catch (error) {
      console.error('Error deleting channel SKU:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete');
    } finally {
      setChannelSkusLoading(false);
    }
  };
  
  // Load channel SKUs when product SKU is available
  useEffect(() => {
    if (productData.sku || productData.master_sku) {
      fetchChannelSkus();
    }
  }, [productData.sku, productData.master_sku]);
  
  // Get label for a channel value
  const getChannelLabel = (channelValue) => {
    const channel = CHANNEL_OPTIONS.find(c => c.value === channelValue);
    return channel?.label || channelValue;
  };
  
  // ============================================================

  // Calculate reorder status
  const getReorderStatus = () => {
    const currentStock = productData.current_stock || 0;
    const reorderPoint = productData.reorder_point || 0;
    
    if (currentStock < reorderPoint) {
      return { status: 'REORDER_REQUIRED', color: 'destructive' };
    } else if (currentStock < reorderPoint * 1.2) {
      return { status: 'LOW_STOCK', color: 'warning' };
    }
    return { status: 'IN_STOCK', color: 'default' };
  };

  // Calculate shelf life alerts
  const getShelfLifeAlert = () => {
    if (!productData.expiry_date) return null;
    
    const expiryDate = new Date(productData.expiry_date);
    const today = new Date();
    const daysRemaining = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
    
    if (daysRemaining < 30) {
      return { alert: true, daysRemaining, color: 'destructive' };
    } else if (daysRemaining < 90) {
      return { alert: true, daysRemaining, color: 'warning' };
    }
    return { alert: false, daysRemaining, color: 'default' };
  };

  // Load product data
  useEffect(() => {
    if (productId) {
      // Load existing product data
      // fetchProductData();
    }
  }, [productId]);

  const reorderStatus = getReorderStatus();
  const shelfLifeAlert = getShelfLifeAlert();

  return (
    <div className="max-w-none w-full space-y-3">
      {/* General Information Tab */}
      <TabsContent value="general">
          <div className="space-y-3">
            <h3 className="text-white text-xs font-medium flex items-center gap-2 mb-2">
              <Package className="w-4 h-4 text-blue-400" />
              General Information
            </h3>
            
            {/* Validation Errors - Compact */}
            {validationErrors.length > 0 && (
              <div className="p-2 bg-red-900/20 border border-red-700/50 rounded-lg mb-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-medium text-red-300 text-xs mb-1">
                      Fix {validationErrors.length} error{validationErrors.length > 1 ? 's' : ''}:
                    </h4>
                    <ul className="text-[10px] text-red-400 list-disc list-inside space-y-0.5">
                      {validationErrors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
            
            {/* Compact 2-column grid */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">
                  Product Name <span className="text-red-400">*</span>
                </label>
                <input
                  id="name"
                  type="text"
                  value={productData.name}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                  placeholder="Enter product name"
                  className={`w-full dark-input ${validationErrors.name ? 'border-red-500' : ''}`}
                />
              </div>
              <div>
                <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">Brand</label>
                <select 
                  id="brand"
                  value={productData.brand || ''}
                  onChange={(e) => handleFieldChange('brand', e.target.value)}
                  className="w-full dark-select"
                >
                  <option value="">Select Brand</option>
                  {brands.map(brand => (
                    <option key={brand} value={brand}>{brand}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">
                  Category <span className="text-red-400">*</span>
                </label>
                <select 
                  id="category"
                  value={productData.category || ''}
                  onChange={(e) => handleFieldChange('category', e.target.value)}
                  className={`w-full dark-select ${validationErrors.some(e => e.includes('Category')) ? 'border-red-500' : ''}`}
                >
                  <option value="">Select Category</option>
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">
                  Subcategory <span className="text-red-400">*</span>
                </label>
                <select 
                  id="subcategory"
                  value={productData.subcategory || ''}
                  onChange={(e) => handleFieldChange('subcategory', e.target.value)}
                  className={`w-full dark-select ${validationErrors.some(e => e.includes('Subcategory')) ? 'border-red-500' : ''}`}
                >
                  <option value="">Select Subcategory</option>
                  {subcategories.map(subcategory => (
                    <option key={subcategory} value={subcategory}>{subcategory}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">Default Warehouse</label>
                <select 
                  id="default_warehouse"
                  value={productData.default_warehouse || ''}
                  onChange={(e) => handleFieldChange('default_warehouse', e.target.value)}
                  className="w-full dark-select"
                >
                  <option value="">Select Warehouse</option>
                  <option value="fba">🟣 FBA (Amazon)</option>
                  <option value="awd">🟢 AWD (Walmart)</option>
                  <option value="garage">🔵 Garage (Internal)</option>
                </select>
              </div>
              <div>
                <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">Status</label>
                <select 
                  id="status"
                  value={productData.status || 'ACTIVE'}
                  onChange={(e) => handleFieldChange('status', e.target.value)}
                  className="w-full dark-select"
                >
                  <option value="ACTIVE">Active</option>
                  <option value="ARCHIVED">Archived</option>
                  <option value="DRAFT">Draft</option>
                </select>
              </div>
              {/* Amazon ASIN - For BSR Tracking */}
              <div>
                <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">
                  Amazon ASIN
                  <span className="ml-1 text-[9px] text-blue-400 font-normal">(for BSR)</span>
                </label>
                <input
                  id="amazon_asin"
                  type="text"
                  maxLength={10}
                  value={productData.amazon_asin || ''}
                  onChange={(e) => handleFieldChange('amazon_asin', e.target.value.toUpperCase())}
                  placeholder="B0XXXXXXXX"
                  className="w-full dark-input font-mono text-[11px]"
                  data-testid="amazon-asin-input"
                />
              </div>
              {/* Description - Full Width */}
              <div className="col-span-2">
                <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">Description</label>
                <textarea
                  id="description"
                  value={productData.description}
                  onChange={(e) => handleFieldChange('description', e.target.value)}
                  placeholder="Enter product description"
                  rows={3}
                  className="w-full dark-textarea"
                />
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Purchasing Tab - Compact Dark Theme */}
        <TabsContent value="purchasing">
          <div className="space-y-4">
            {/* Basic Purchasing Info Section */}
            <div className="space-y-3">
              <h3 className="text-white text-xs font-medium flex items-center gap-2 mb-2">
                <ShoppingCart className="w-4 h-4 text-blue-400" />
                Default Purchasing Settings
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">Purchase UOM</label>
                  <select 
                    id="purchase_uom"
                    value={productData.purchase_uom || 'each'}
                    onChange={(e) => handleFieldChange('purchase_uom', e.target.value)}
                    className="w-full text-xs h-8 px-2 bg-[#0d1b2a] text-white border border-gray-700 rounded focus:outline-none focus:border-blue-500"
                  >
                    <option value="each">Each</option>
                    <option value="piece">Piece</option>
                    <option value="pack">Pack</option>
                    <option value="case">Case</option>
                    <option value="box">Box</option>
                    <option value="pallet">Pallet</option>
                    <option value="dozen">Dozen</option>
                    <option value="oz">Ounce</option>
                    <option value="lb">Pound</option>
                    <option value="g">Gram</option>
                    <option value="kg">Kilogram</option>
                    <option value="ml">Milliliter</option>
                    <option value="l">Liter</option>
                  </select>
                </div>
                <div>
                  <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">Default Min Order Qty</label>
                  <input
                    id="minimum_order_qty"
                    type="number"
                    value={productData.minimum_order_qty}
                    onChange={(e) => handleFieldChange('minimum_order_qty', parseInt(e.target.value))}
                    placeholder="1"
                    className="w-full text-xs h-8 px-2 bg-[#0d1b2a] text-white border border-gray-700 rounded focus:outline-none focus:border-blue-500 placeholder:text-gray-500"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">Default Lead Time (Days)</label>
                  <input
                    id="lead_time_days"
                    type="number"
                    value={productData.lead_time_days}
                    onChange={(e) => handleFieldChange('lead_time_days', parseInt(e.target.value))}
                    placeholder="7"
                    className="w-full text-xs h-8 px-2 bg-[#0d1b2a] text-white border border-gray-700 rounded focus:outline-none focus:border-blue-500 placeholder:text-gray-500"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">Last Purchase Date</label>
                  <input
                    id="last_purchase_date"
                    type="date"
                    value={productData.last_purchase_date}
                    onChange={(e) => handleFieldChange('last_purchase_date', e.target.value)}
                    className="w-full text-xs h-8 px-2 bg-[#0d1b2a] text-white border border-gray-700 rounded focus:outline-none focus:border-blue-500"
                  />
                </div>
              </div>
            </div>
            
            {/* Supplier SKU Mappings Section */}
            <SupplierSkuManager 
              productId={productId || item?.id}
              productSku={productData.sku || item?.sku}
              onMappingChange={() => {
                setHasUnsavedChanges(true);
                onDataChange();
              }}
            />
          </div>
        </TabsContent>

        {/* OLD TABS - REMOVED AND CONSOLIDATED INTO INVENTORY TAB */}
        {/* Planning Tab - Now consolidated into Inventory */}
        {/*
        <TabsContent value="planning">
          ... content removed ...
        </TabsContent>
        */}

        {/* Sales Tab - Compact Dark Theme */}
        <TabsContent value="sales">
          <div className="space-y-3">
            <h3 className="text-white text-xs font-medium flex items-center gap-2 mb-2">
              <DollarSign className="w-4 h-4 text-green-400" />
              Sales Information
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">Selling UOM</label>
                <select 
                  id="selling_uom"
                  value={productData.selling_uom || 'each'}
                  onChange={(e) => handleFieldChange('selling_uom', e.target.value)}
                  className="w-full text-xs h-8 px-2 bg-[#0d1b2a] text-white border border-gray-700 rounded focus:outline-none focus:border-blue-500"
                >
                  <option value="each">Each</option>
                  <option value="piece">Piece</option>
                  <option value="pack">Pack</option>
                  <option value="set">Set</option>
                  <option value="case">Case</option>
                  <option value="box">Box</option>
                  <option value="dozen">Dozen</option>
                  <option value="oz">Ounce</option>
                  <option value="lb">Pound</option>
                  <option value="g">Gram</option>
                  <option value="kg">Kilogram</option>
                  <option value="ml">Milliliter</option>
                  <option value="l">Liter</option>
                </select>
              </div>
              <div>
                <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">Default Selling Price ($)</label>
                <input
                  id="default_selling_price"
                  type="number"
                  step="0.01"
                  value={productData.default_selling_price}
                  onChange={(e) => handleFieldChange('default_selling_price', parseFloat(e.target.value))}
                  placeholder="0.00"
                  className="w-full text-xs h-8 px-2 bg-[#0d1b2a] text-white border border-gray-700 rounded focus:outline-none focus:border-blue-500 placeholder:text-gray-500"
                />
              </div>
              <div>
                <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">Channel Assignment</label>
                <select 
                  id="channel_assignment"
                  value={productData.channel_assignment || ''}
                  onChange={(e) => handleFieldChange('channel_assignment', e.target.value)}
                  className="w-full text-xs h-8 px-2 bg-[#0d1b2a] text-white border border-gray-700 rounded focus:outline-none focus:border-blue-500"
                >
                  <option value="">Select Channel</option>
                  {channels.map(channel => (
                    <option key={channel} value={channel}>{channel}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">Gross Margin (Auto)</label>
                <div className="h-8 px-2 bg-[#0d1b2a] border border-gray-700 rounded flex items-center justify-between">
                  <span className="text-sm font-semibold text-green-400">
                    {(((Number(productData.default_selling_price) || 0) - (Number(productData.purchase_price) || 0)) / (Number(productData.default_selling_price) || 1) * 100).toFixed(1)}%
                  </span>
                  <span className="text-[10px] text-gray-500">
                    ${productData.default_selling_price} - ${productData.purchase_price}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>


        {/* Inventory Tab - Compact Dark Theme */}
        <TabsContent value="inventory">
          <div className="space-y-4">
            <h3 className="text-white text-xs font-medium flex items-center gap-2">
              <Warehouse className="w-4 h-4 text-purple-400" />
              Inventory Management
            </h3>
            
            {/* Section 1: Stock Levels & Reorder */}
            <div className="space-y-2">
              <h4 className="text-gray-400 text-[10px] uppercase tracking-wide flex items-center gap-1.5 pb-1 border-b border-gray-700">
                📦 Stock Levels & Reorder
              </h4>
              
              {/* Compact Reorder Alert */}
              {reorderStatus.status !== 'IN_STOCK' && (
                <div className="p-2 bg-red-900/30 border border-red-700/50 rounded flex items-center gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
                  <span className="text-[10px] text-red-300">
                    {reorderStatus.status.replace('_', ' ')} • Stock: {productData.current_stock || 0} / Reorder: {productData.reorder_point || 0}
                  </span>
                </div>
              )}

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">Current Stock</label>
                  <input
                    id="current_stock"
                    type="number"
                    value={productData.current_stock || 0}
                    onChange={(e) => handleFieldChange('current_stock', parseInt(e.target.value))}
                    placeholder="0"
                    className="w-full text-xs h-8 bg-[#0d1b2a] text-white border border-gray-700 rounded"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">Reorder Point</label>
                  <input
                    id="reorder_point"
                    type="number"
                    value={productData.reorder_point}
                    onChange={(e) => handleFieldChange('reorder_point', parseInt(e.target.value))}
                    placeholder="10"
                    className="w-full text-xs h-8 bg-[#0d1b2a] text-white border border-gray-700 rounded"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">Target Level</label>
                  <input
                    id="target_stock_level"
                    type="number"
                    value={productData.target_stock_level}
                    onChange={(e) => handleFieldChange('target_stock_level', parseInt(e.target.value))}
                    placeholder="50"
                    className="w-full text-xs h-8 bg-[#0d1b2a] text-white border border-gray-700 rounded"
                  />
                </div>
              </div>
            </div>

            {/* Section 2: Warehouse & Storage */}
            <div className="space-y-2">
              <h4 className="text-gray-400 text-[10px] uppercase tracking-wide flex items-center gap-1.5 pb-1 border-b border-gray-700">
                🏭 Warehouse & Storage
              </h4>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">Warehouse Code</label>
                  <input
                    id="warehouse_code"
                    value={productData.warehouse_code || ''}
                    onChange={(e) => handleFieldChange('warehouse_code', e.target.value)}
                    placeholder="WHG-001"
                    className="w-full text-xs h-8 bg-[#0d1b2a] text-white border border-gray-700 rounded placeholder:text-gray-500"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">Storage Location</label>
                  <input
                    id="storage_location"
                    value={productData.storage_location}
                    onChange={(e) => handleFieldChange('storage_location', e.target.value)}
                    placeholder="A1-B2-C3"
                    className="w-full text-xs h-8 bg-[#0d1b2a] text-white border border-gray-700 rounded placeholder:text-gray-500"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">Storage Condition</label>
                  <select 
                    id="storage_condition"
                    value={productData.storage_condition || 'room_temperature'}
                    onChange={(e) => handleFieldChange('storage_condition', e.target.value)}
                    className="w-full text-xs h-8 px-2 bg-[#0d1b2a] text-white border border-gray-700 rounded focus:outline-none focus:border-blue-500"
                  >
                    <option value="room_temperature">Room Temperature</option>
                    <option value="refrigerated">Refrigerated</option>
                    <option value="frozen">Frozen</option>
                    <option value="climate_controlled">Climate Controlled</option>
                    <option value="dry_storage">Dry Storage</option>
                  </select>
                </div>
                <div>
                  <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">Preferred Warehouse</label>
                  <select 
                    id="preferred_warehouse"
                    value={productData.preferred_warehouse || ''}
                    onChange={(e) => handleFieldChange('preferred_warehouse', e.target.value)}
                    className="w-full text-xs h-8 px-2 bg-[#0d1b2a] text-white border border-gray-700 rounded focus:outline-none focus:border-blue-500"
                  >
                    <option value="">Select Warehouse</option>
                    <option value="fba">🟣 FBA (Amazon)</option>
                    <option value="awd">🟢 AWD (Walmart)</option>
                    <option value="garage">🔵 Garage (Internal)</option>
                  </select>
                </div>
                <div>
                  <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">Fulfillment Channel</label>
                  <select 
                    id="fulfillment_channel"
                    data-testid="fulfillment-channel-select"
                    value={productData.fulfillment_channel || ''}
                    onChange={(e) => handleFieldChange('fulfillment_channel', e.target.value)}
                    className="w-full text-xs h-8 px-2 bg-[#0d1b2a] text-white border border-gray-700 rounded focus:outline-none focus:border-blue-500"
                  >
                    <option value="">-- Select --</option>
                    <option value="fba">🟣 FBA (Amazon)</option>
                    <option value="Fulex 3PL">🔶 Fulex 3PL</option>
                    <option value="inhouse">🏠 In-House</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Section 3: Dimensions & Weight */}
            <div className="space-y-2">
              <h4 className="text-gray-400 text-[10px] uppercase tracking-wide flex items-center gap-1.5 pb-1 border-b border-gray-700">
                📏 Dimensions & Weight
              </h4>
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">Length</label>
                  <div className="flex gap-1">
                    <input
                      id="length"
                      type="number"
                      value={productData.length || ''}
                      onChange={(e) => handleFieldChange('length', parseFloat(e.target.value))}
                      placeholder="0"
                      className="flex-1 text-xs h-8 bg-[#0d1b2a] text-white border border-gray-700 rounded"
                    />
                    <select
                      value={productData.dimension_unit || 'in'}
                      onChange={(e) => handleFieldChange('dimension_unit', e.target.value)}
                      className="w-12 text-[10px] h-8 px-1 bg-[#0d1b2a] text-white border border-gray-700 rounded focus:outline-none"
                    >
                      <option value="in">in</option>
                      <option value="cm">cm</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">Width</label>
                  <div className="flex gap-1">
                    <input
                      id="width"
                      type="number"
                      value={productData.width || ''}
                      onChange={(e) => handleFieldChange('width', parseFloat(e.target.value))}
                      placeholder="0"
                      className="flex-1 text-xs h-8 bg-[#0d1b2a] text-white border border-gray-700 rounded"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">Height</label>
                  <div className="flex gap-1">
                    <input
                      id="height"
                      type="number"
                      value={productData.height || ''}
                      onChange={(e) => handleFieldChange('height', parseFloat(e.target.value))}
                      placeholder="0"
                      className="flex-1 text-xs h-8 bg-[#0d1b2a] text-white border border-gray-700 rounded"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">Weight</label>
                  <div className="flex gap-1">
                    <input
                      id="weight"
                      type="number"
                      value={productData.weight || ''}
                      onChange={(e) => handleFieldChange('weight', parseFloat(e.target.value))}
                      placeholder="0"
                      className="flex-1 text-xs h-8 bg-[#0d1b2a] text-white border border-gray-700 rounded"
                    />
                    <select
                      value={productData.weight_unit || 'lb'}
                      onChange={(e) => handleFieldChange('weight_unit', e.target.value)}
                      className="w-12 text-[10px] h-8 px-1 bg-[#0d1b2a] text-white border border-gray-700 rounded focus:outline-none"
                    >
                      <option value="lb">lb</option>
                      <option value="kg">kg</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 4: Packaging */}
            <div className="space-y-2">
              <h4 className="text-gray-400 text-[10px] uppercase tracking-wide flex items-center gap-1.5 pb-1 border-b border-gray-700">
                📦 Packaging
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">Units per Package</label>
                  <input
                    id="units_per_package"
                    type="number"
                    value={productData.units_per_package || 1}
                    onChange={(e) => handleFieldChange('units_per_package', parseInt(e.target.value))}
                    placeholder="1"
                    className="w-full text-xs h-8 bg-[#0d1b2a] text-white border border-gray-700 rounded"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">Packaging Material</label>
                  <input
                    id="packaging_material"
                    value={productData.packaging_material || ''}
                    onChange={(e) => handleFieldChange('packaging_material', e.target.value)}
                    placeholder="Cardboard, Plastic..."
                    className="w-full text-xs h-8 bg-[#0d1b2a] text-white border border-gray-700 rounded placeholder:text-gray-500"
                  />
                </div>
              </div>
            </div>

            {/* Section 5: Shelf Life (Optional) */}
            <div className="space-y-2">
              <h4 className="text-gray-400 text-[10px] uppercase tracking-wide flex items-center gap-1.5 pb-1 border-b border-gray-700">
                📅 Shelf Life (Optional)
              </h4>
              
              {/* Compact Shelf Life Alert */}
              {shelfLifeAlert && shelfLifeAlert.alert && (
                <div className={`p-2 rounded flex items-center gap-2 ${
                  shelfLifeAlert.color === 'destructive' 
                    ? 'bg-red-900/30 border border-red-700/50' 
                    : 'bg-yellow-900/30 border border-yellow-700/50'
                }`}>
                  <AlertTriangle className={`w-3.5 h-3.5 ${
                    shelfLifeAlert.color === 'destructive' ? 'text-red-400' : 'text-yellow-400'
                  }`} />
                  <span className={`text-[10px] ${
                    shelfLifeAlert.color === 'destructive' ? 'text-red-300' : 'text-yellow-300'
                  }`}>
                    Expiry Alert: {shelfLifeAlert.daysRemaining} days remaining
                  </span>
                </div>
              )}

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">Batch Number</label>
                  <input
                    id="batch_number"
                    value={productData.batch_number || ''}
                    onChange={(e) => handleFieldChange('batch_number', e.target.value)}
                    placeholder="BATCH-001"
                    className="w-full text-xs h-8 bg-[#0d1b2a] text-white border border-gray-700 rounded placeholder:text-gray-500"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">Mfg Date</label>
                  <input
                    id="manufacturing_date"
                    type="date"
                    value={productData.manufacturing_date || ''}
                    onChange={(e) => handleFieldChange('manufacturing_date', e.target.value)}
                    className="w-full text-xs h-8 bg-[#0d1b2a] text-white border border-gray-700 rounded"
                  />
                </div>
                <div>
                  <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">Expiry Date</label>
                  <input
                    id="expiry_date"
                    type="date"
                    value={productData.expiry_date || ''}
                    onChange={(e) => handleFieldChange('expiry_date', e.target.value)}
                    className="w-full text-xs h-8 bg-[#0d1b2a] text-white border border-gray-700 rounded"
                  />
                </div>
              </div>
            </div>

            {/* Section 6: Master Carton & Pallet Specs */}
            <div className="space-y-2 border-t border-cyan-700/30 pt-4 mt-4">
              <h4 className="text-cyan-400 text-[10px] uppercase tracking-wide flex items-center gap-1.5 pb-1 border-b border-cyan-700/30">
                📦 Master Carton & Pallet Specs
                <span className="text-[9px] text-gray-500 font-normal ml-1">(for 3PL shipment planning)</span>
              </h4>
                
                {/* Master Carton Group */}
                <div className="space-y-1.5">
                  <span className="text-gray-500 text-[9px] uppercase tracking-wider">Master Carton</span>
                  <div className="grid grid-cols-5 gap-2">
                    <div>
                      <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">Units/Carton</label>
                      <input
                        id="units_per_carton"
                        data-testid="carton-units-per-carton"
                        type="number"
                        value={productData.carton_specs?.units_per_carton || ''}
                        onChange={(e) => handleCartonSpecsChange('units_per_carton', parseInt(e.target.value) || 0)}
                        placeholder="0"
                        className="w-full text-xs h-8 bg-[#0d1b2a] text-cyan-300 border border-cyan-700/50 rounded focus:border-cyan-500"
                      />
                    </div>
                    <div>
                      <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">Length (in)</label>
                      <input
                        id="carton_length_in"
                        data-testid="carton-length"
                        type="number"
                        step="0.1"
                        value={productData.carton_specs?.carton_length_in || ''}
                        onChange={(e) => handleCartonSpecsChange('carton_length_in', parseFloat(e.target.value) || 0)}
                        placeholder="0"
                        className="w-full text-xs h-8 bg-[#0d1b2a] text-cyan-300 border border-cyan-700/50 rounded focus:border-cyan-500"
                      />
                    </div>
                    <div>
                      <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">Width (in)</label>
                      <input
                        id="carton_width_in"
                        data-testid="carton-width"
                        type="number"
                        step="0.1"
                        value={productData.carton_specs?.carton_width_in || ''}
                        onChange={(e) => handleCartonSpecsChange('carton_width_in', parseFloat(e.target.value) || 0)}
                        placeholder="0"
                        className="w-full text-xs h-8 bg-[#0d1b2a] text-cyan-300 border border-cyan-700/50 rounded focus:border-cyan-500"
                      />
                    </div>
                    <div>
                      <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">Height (in)</label>
                      <input
                        id="carton_height_in"
                        data-testid="carton-height"
                        type="number"
                        step="0.1"
                        value={productData.carton_specs?.carton_height_in || ''}
                        onChange={(e) => handleCartonSpecsChange('carton_height_in', parseFloat(e.target.value) || 0)}
                        placeholder="0"
                        className="w-full text-xs h-8 bg-[#0d1b2a] text-cyan-300 border border-cyan-700/50 rounded focus:border-cyan-500"
                      />
                    </div>
                    <div>
                      <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">Weight (lb)</label>
                      <input
                        id="carton_weight_lb"
                        data-testid="carton-weight"
                        type="number"
                        step="0.1"
                        value={productData.carton_specs?.carton_weight_lb || ''}
                        onChange={(e) => handleCartonSpecsChange('carton_weight_lb', parseFloat(e.target.value) || 0)}
                        placeholder="0"
                        className="w-full text-xs h-8 bg-[#0d1b2a] text-cyan-300 border border-cyan-700/50 rounded focus:border-cyan-500"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Pallet Group */}
                <div className="space-y-1.5 mt-3">
                  <span className="text-gray-500 text-[9px] uppercase tracking-wider">Pallet Configuration</span>
                  <div className="grid grid-cols-4 gap-2">
                    <div>
                      <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block flex items-center gap-1">
                        Cartons/Pallet
                        {!cartonsPerPalletOverride && autoCartonsPerPallet > 0 && (
                          <span className="text-cyan-500 text-[9px]">(auto)</span>
                        )}
                      </label>
                      <input
                        id="cartons_per_pallet"
                        data-testid="cartons-per-pallet"
                        type="number"
                        value={displayCartonsPerPallet || ''}
                        onChange={(e) => {
                          const newValue = parseInt(e.target.value) || 0;
                          // If user types a value different from auto, mark as override
                          if (newValue !== autoCartonsPerPallet && newValue > 0) {
                            setCartonsPerPalletOverride(true);
                          }
                          // If user clears or enters 0, check if we should revert to auto
                          if (newValue === 0 || e.target.value === '') {
                            setCartonsPerPalletOverride(false);
                          }
                          handleCartonSpecsChange('cartons_per_pallet', newValue);
                        }}
                        onBlur={(e) => {
                          // If field is empty or 0 and auto is available, revert to auto
                          const val = parseInt(e.target.value) || 0;
                          if (val === 0 && autoCartonsPerPallet > 0) {
                            setCartonsPerPalletOverride(false);
                            handleCartonSpecsChange('cartons_per_pallet', autoCartonsPerPallet);
                          }
                        }}
                        placeholder={autoCartonsPerPallet > 0 ? autoCartonsPerPallet.toString() : "0"}
                        className={`w-full text-xs h-8 px-2 rounded focus:border-cyan-500 ${
                          !cartonsPerPalletOverride && autoCartonsPerPallet > 0
                            ? 'bg-cyan-900/20 text-cyan-400 border border-cyan-700/30'
                            : 'bg-[#0d1b2a] text-white border border-gray-700'
                        }`}
                      />
                    </div>
                    <div>
                      <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">Pallet Height (in)</label>
                      <input
                        id="pallet_height_in"
                        data-testid="pallet-height"
                        type="number"
                        step="0.1"
                        value={productData.carton_specs?.pallet_height_in || ''}
                        onChange={(e) => handleCartonSpecsChange('pallet_height_in', parseFloat(e.target.value) || 0)}
                        placeholder="0"
                        className="w-full text-xs h-8 px-2 bg-[#0d1b2a] text-cyan-300 border border-cyan-700/50 rounded focus:border-cyan-500"
                      />
                    </div>
                    <div>
                      <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">Pallet Weight (lb)</label>
                      <input
                        id="pallet_weight_lb"
                        data-testid="pallet-weight"
                        type="number"
                        step="0.1"
                        value={productData.carton_specs?.pallet_weight_lb || ''}
                        onChange={(e) => handleCartonSpecsChange('pallet_weight_lb', parseFloat(e.target.value) || 0)}
                        placeholder="0"
                        className="w-full text-xs h-8 px-2 bg-[#0d1b2a] text-cyan-300 border border-cyan-700/50 rounded focus:border-cyan-500"
                      />
                    </div>
                    <div>
                      <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block flex items-center gap-1">
                        Units/Pallet
                        <span className="text-cyan-500 text-[9px]">(auto)</span>
                      </label>
                      <div className="w-full text-xs h-8 px-2 bg-cyan-900/20 text-cyan-400 border border-cyan-700/30 rounded flex items-center font-medium">
                        {(productData.carton_specs?.units_per_carton || 0) * (displayCartonsPerPallet || 0)}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Notes */}
                <div className="mt-3">
                  <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">Notes</label>
                  <input
                    id="carton_specs_notes"
                    data-testid="carton-specs-notes"
                    type="text"
                    value={productData.carton_specs?.notes || ''}
                    onChange={(e) => handleCartonSpecsChange('notes', e.target.value)}
                    placeholder="e.g., 48x40 standard pallet"
                    className="w-full text-xs h-8 bg-[#0d1b2a] text-white border border-gray-700 rounded placeholder:text-gray-500"
                  />
                </div>
              </div>
            </div>
        </TabsContent>

        {/* Storage Tab - Now consolidated into Inventory */}
        {/*
        <TabsContent value="storage">
          ... content removed ...
        </TabsContent>
        */}

        {/* REMOVED: Shelf Life Tab - Now merged into Storage tab above */}

        {/* Packaging Tab - Now consolidated into Inventory */}
        {/*
        <TabsContent value="packaging">
          ... content removed ...
        </TabsContent>
        */}

        {/* BOM (Bill of Materials) Tab - Only for Finished Products */}
        {productData.product_type !== 'raw_material' && (
          <TabsContent value="bom">
            <div className="space-y-4">
              <BOMManager 
                productId={productId}
                productType={productData.product_type || 'finished_product'}
                onCostUpdate={() => {
                  // Refresh product data when costs change
                  console.log('BOM costs updated, refreshing product data...');
                }}
              />
            </div>
          </TabsContent>
        )}

        {/* Pricing Tab - New BOM-Integrated Version */}
        <TabsContent value="pricing">
          <PricingTab
            productId={productId}
            productData={productData}
            onFieldChange={handleFieldChange}
            onNavigateToBOM={() => setActiveTab('bom')}
          />
        </TabsContent>

        {/* ============================================================ */}
        {/* CHANNELS TAB - Channel SKU Architecture */}
        {/* ============================================================ */}
        <TabsContent value="channels">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-white text-xs font-medium flex items-center gap-2">
                <Link2 className="w-4 h-4 text-purple-400" />
                Channel SKU Mappings
              </h3>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400">
                  Master SKU: <span className="text-white font-mono">{productData.master_sku || productData.sku || 'N/A'}</span>
                </span>
                <Button
                  size="sm"
                  onClick={() => setShowAddChannelForm(!showAddChannelForm)}
                  className="h-7 px-2 text-xs bg-purple-600 hover:bg-purple-700"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Add Mapping
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={fetchChannelSkus}
                  disabled={channelSkusLoading}
                  className="h-7 px-2 text-xs border-gray-600"
                >
                  <RefreshCw className={`w-3 h-3 ${channelSkusLoading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>
            
            {/* Add New Channel SKU Form */}
            {showAddChannelForm && (
              <div className="p-3 bg-purple-900/20 border border-purple-700/50 rounded-lg">
                <div className="grid grid-cols-5 gap-3 items-end">
                  <div>
                    <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">Channel</label>
                    <select
                      value={newChannelSku.channel}
                      onChange={(e) => setNewChannelSku(prev => ({ ...prev, channel: e.target.value }))}
                      className="w-full text-xs h-8 px-2 bg-[#0d1b2a] text-white border border-gray-700 rounded focus:outline-none focus:border-purple-500"
                    >
                      {CHANNEL_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">Channel SKU *</label>
                    <input
                      type="text"
                      value={newChannelSku.channel_sku}
                      onChange={(e) => setNewChannelSku(prev => ({ ...prev, channel_sku: e.target.value }))}
                      placeholder={`e.g., ${CHANNEL_OPTIONS.find(c => c.value === newChannelSku.channel)?.prefix || ''}${productData.sku || '4752'}`}
                      className="w-full text-xs h-8 px-2 bg-[#0d1b2a] text-white border border-gray-700 rounded focus:outline-none focus:border-purple-500 placeholder:text-gray-500"
                    />
                  </div>
                  <div>
                    <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">ASIN (Amazon)</label>
                    <input
                      type="text"
                      value={newChannelSku.asin}
                      onChange={(e) => setNewChannelSku(prev => ({ ...prev, asin: e.target.value }))}
                      placeholder="B0XXXXXXXX"
                      className="w-full text-xs h-8 px-2 bg-[#0d1b2a] text-white border border-gray-700 rounded focus:outline-none focus:border-purple-500 placeholder:text-gray-500"
                    />
                  </div>
                  <div>
                    <label className="text-gray-400 text-[10px] uppercase tracking-wide mb-0.5 block">Status</label>
                    <select
                      value={newChannelSku.status}
                      onChange={(e) => setNewChannelSku(prev => ({ ...prev, status: e.target.value }))}
                      className="w-full text-xs h-8 px-2 bg-[#0d1b2a] text-white border border-gray-700 rounded focus:outline-none focus:border-purple-500"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={handleAddChannelSku}
                      disabled={channelSkusLoading || !newChannelSku.channel_sku.trim()}
                      className="h-8 px-3 text-xs bg-green-600 hover:bg-green-700"
                    >
                      <Check className="w-3 h-3 mr-1" />
                      Add
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setShowAddChannelForm(false);
                        setNewChannelSku({ channel: 'amazon_fba', channel_sku: '', asin: '', status: 'active' });
                      }}
                      className="h-8 px-3 text-xs border-gray-600"
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Channel SKUs Table */}
            <div className="bg-[#0d1b2a] border border-gray-700 rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-800/50">
                    <th className="text-left text-gray-400 text-[10px] uppercase tracking-wide p-2 w-32">Channel</th>
                    <th className="text-left text-gray-400 text-[10px] uppercase tracking-wide p-2">Channel SKU</th>
                    <th className="text-left text-gray-400 text-[10px] uppercase tracking-wide p-2 w-28">ASIN</th>
                    <th className="text-center text-gray-400 text-[10px] uppercase tracking-wide p-2 w-20">Active</th>
                    <th className="text-center text-gray-400 text-[10px] uppercase tracking-wide p-2 w-20">Source</th>
                    <th className="text-center text-gray-400 text-[10px] uppercase tracking-wide p-2 w-24">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {channelSkusLoading && channelSkus.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="p-4 text-center text-gray-500">
                        <RefreshCw className="w-4 h-4 animate-spin inline mr-2" />
                        Loading...
                      </td>
                    </tr>
                  ) : channelSkus.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="p-4 text-center text-gray-500">
                        No channel SKU mappings found. Click &quot;Add Mapping&quot; to create one.
                      </td>
                    </tr>
                  ) : (
                    channelSkus.map((mapping, index) => {
                      // Clean channel_sku - remove .0 suffix from numeric SKUs
                      const cleanSku = String(mapping.channel_sku || '').replace(/\.0$/, '');
                      
                      return (
                      <tr 
                        key={mapping.id || cleanSku} 
                        className={`border-t border-gray-700/50 ${index % 2 === 0 ? 'bg-gray-800/20' : ''} hover:bg-gray-700/30`}
                      >
                        {/* Channel */}
                        <td className="p-2">
                          {editingChannelSku === cleanSku ? (
                            <select
                              defaultValue={mapping.channel}
                              id={`edit-channel-${cleanSku}`}
                              className="w-full text-xs h-7 px-1 bg-[#0d1b2a] text-white border border-purple-500 rounded focus:outline-none"
                            >
                              {CHANNEL_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          ) : (
                            <span className="text-white">{getChannelLabel(mapping.channel)}</span>
                          )}
                        </td>
                        
                        {/* Channel SKU */}
                        <td className="p-2">
                          {editingChannelSku === cleanSku ? (
                            <input
                              type="text"
                              defaultValue={cleanSku}
                              id={`edit-sku-${cleanSku}`}
                              className="w-full text-xs h-7 px-2 bg-[#0d1b2a] text-white border border-purple-500 rounded focus:outline-none font-mono"
                            />
                          ) : (
                            <span className="text-white font-mono">{cleanSku}</span>
                          )}
                        </td>
                        
                        {/* ASIN */}
                        <td className="p-2">
                          {editingChannelSku === mapping.channel_sku ? (
                            <input
                              type="text"
                              defaultValue={mapping.asin || ''}
                              id={`edit-asin-${cleanSku}`}
                              placeholder="-"
                              className="w-full text-xs h-7 px-2 bg-[#0d1b2a] text-white border border-purple-500 rounded focus:outline-none font-mono placeholder:text-gray-500"
                            />
                          ) : (
                            <span className="text-gray-300 font-mono">{mapping.asin || '-'}</span>
                          )}
                        </td>
                        
                        {/* Active Toggle */}
                        <td className="p-2 text-center">
                          {editingChannelSku === cleanSku ? (
                            <select
                              defaultValue={mapping.status || 'active'}
                              id={`edit-status-${cleanSku}`}
                              className="text-xs h-7 px-1 bg-[#0d1b2a] text-white border border-purple-500 rounded focus:outline-none"
                            >
                              <option value="active">✓</option>
                              <option value="inactive">✗</option>
                            </select>
                          ) : (
                            <span className={mapping.status === 'active' || !mapping.status ? 'text-green-400' : 'text-gray-500'}>
                              {mapping.status === 'active' || !mapping.status ? '✓' : '✗'}
                            </span>
                          )}
                        </td>
                        
                        {/* Source */}
                        <td className="p-2 text-center">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${mapping.auto_created ? 'bg-blue-900/30 text-blue-400' : 'bg-purple-900/30 text-purple-400'}`}>
                            {mapping.auto_created ? 'Auto' : 'Manual'}
                          </span>
                        </td>
                        
                        {/* Actions */}
                        <td className="p-2 text-center">
                          {editingChannelSku === cleanSku ? (
                            <div className="flex justify-center gap-1">
                              <Button
                                size="sm"
                                onClick={() => {
                                  const channel = document.getElementById(`edit-channel-${cleanSku}`)?.value;
                                  const newChannelSkuVal = document.getElementById(`edit-sku-${cleanSku}`)?.value;
                                  const asin = document.getElementById(`edit-asin-${cleanSku}`)?.value;
                                  const status = document.getElementById(`edit-status-${cleanSku}`)?.value;
                                  
                                  // Sanitize channel SKU - remove trailing .0 from numeric-looking values
                                  let sanitizedSku = String(newChannelSkuVal || '').trim().replace(/\.0$/, '');
                                  
                                  handleUpdateChannelSku(mapping.channel_sku, { 
                                    channel, 
                                    channel_sku: sanitizedSku !== cleanSku ? sanitizedSku : undefined,
                                    asin, 
                                    status 
                                  });
                                }}
                                className="h-6 w-6 p-0 bg-green-600 hover:bg-green-700"
                              >
                                <Check className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setEditingChannelSku(null)}
                                className="h-6 w-6 p-0 border-gray-600"
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex justify-center gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setEditingChannelSku(cleanSku)}
                                className="h-6 w-6 p-0 text-gray-400 hover:text-white hover:bg-gray-700"
                              >
                                <Edit2 className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteChannelSku(mapping.channel_sku)}
                                className="h-6 w-6 p-0 text-gray-400 hover:text-red-400 hover:bg-red-900/20"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );})
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Info Section */}
            <div className="p-3 bg-blue-900/20 border border-blue-700/50 rounded-lg">
              <h4 className="text-blue-300 text-xs font-medium mb-2 flex items-center gap-1">
                💡 How Channel SKU Mapping Works
              </h4>
              <ul className="text-[10px] text-gray-400 space-y-1">
                <li>• <span className="text-white">Master SKU</span> is your internal product identifier (e.g., 4752)</li>
                <li>• <span className="text-white">Channel SKUs</span> are marketplace-specific (e.g., FBA_4752, WM-4752)</li>
                <li>• When orders come in, the system automatically maps channel SKUs → Master SKU for accurate COGS and profit</li>
                <li>• <span className="text-blue-400">Auto</span> mappings are created automatically during sync; <span className="text-purple-400">Manual</span> mappings are user-created</li>
              </ul>
            </div>
          </div>
        </TabsContent>

        {/* ============================================================ */}
        {/* SUPPLIERS TAB - Supplier SKU Mappings */}
        {/* ============================================================ */}
        <TabsContent value="suppliers">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-white text-xs font-medium flex items-center gap-2">
                <Package className="w-4 h-4 text-orange-400" />
                Supplier SKU Mappings
              </h3>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400">
                  Your SKU: <span className="text-white font-mono">{productData.sku || 'N/A'}</span>
                </span>
              </div>
            </div>
            
            {/* Supplier SKU Table */}
            <div className="bg-[#0d1b2a] rounded-lg overflow-hidden border border-gray-800">
              <table className="w-full text-xs">
                <thead className="bg-[#0a1628]">
                  <tr>
                    <th className="text-left p-2 text-gray-400 font-medium">Supplier</th>
                    <th className="text-left p-2 text-gray-400 font-medium">Their SKU</th>
                    <th className="text-right p-2 text-gray-400 font-medium">Unit Cost</th>
                    <th className="text-center p-2 text-gray-400 font-medium">MOQ</th>
                    <th className="text-center p-2 text-gray-400 font-medium">Lead Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {productData.supplier_skus && productData.supplier_skus.length > 0 ? (
                    productData.supplier_skus.map((mapping, index) => (
                      <tr key={index} className="hover:bg-[#1a2744]/50">
                        <td className="p-2 text-white">{mapping.supplier_name}</td>
                        <td className="p-2 text-orange-400 font-mono">{mapping.supplier_sku}</td>
                        <td className="p-2 text-right text-emerald-400">${(Number(mapping.unit_cost) || 0).toFixed(2)}</td>
                        <td className="p-2 text-center text-gray-300">{mapping.moq || '-'}</td>
                        <td className="p-2 text-center text-gray-300">{mapping.lead_time_days ? `${mapping.lead_time_days} days` : '-'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="p-4 text-center text-gray-500">
                        No supplier SKU mappings yet. Add mappings via API or bulk import.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Info Section */}
            <div className="p-3 bg-orange-900/20 border border-orange-700/50 rounded-lg">
              <h4 className="text-orange-300 text-xs font-medium mb-2 flex items-center gap-1">
                📦 How Supplier SKU Mapping Works
              </h4>
              <ul className="text-[10px] text-gray-400 space-y-1">
                <li>• <span className="text-white">Your SKU</span> is your internal product identifier (e.g., 4723)</li>
                <li>• <span className="text-orange-400">Supplier SKU</span> is what your supplier calls the same product (e.g., 7124)</li>
                <li>• When creating POs, the system shows both SKUs for easy matching</li>
                <li>• <span className="text-emerald-400">Unit Cost</span> helps auto-fill pricing on purchase orders</li>
              </ul>
            </div>
          </div>
        </TabsContent>

      {/* Floating Save Button - Only show when not in fullPageMode */}
      {!fullPageMode && (
        <div className="fixed bottom-6 right-6 z-50">
          <Button
            onClick={handleSaveAll}
            disabled={!hasUnsavedChanges || saveStatus === 'saving'}
            className={`px-6 py-3 rounded-lg shadow-lg transition-all ${
              saveStatus === 'saving' 
                ? 'bg-blue-400 cursor-wait' 
                : saveStatus === 'saved' 
                ? 'bg-green-600 hover:bg-green-700' 
                : saveStatus === 'error'
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {saveStatus === 'saving' ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2 inline-block" />
                Saving...
              </>
            ) : saveStatus === 'saved' ? (
              <>
                <CheckCircle className="w-4 h-4 mr-2 inline-block" />
                Saved!
              </>
            ) : saveStatus === 'error' ? (
              <>
                <AlertTriangle className="w-4 h-4 mr-2 inline-block" />
                Error - Retry
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2 inline-block" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
});

// Add display name for better debugging
ProductMasterDetail.displayName = 'ProductMasterDetail';

export default ProductMasterDetail;